import { FC, useEffect, useState } from 'react'
import { Icon } from '@liveconnect/icons'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { FormProvider, FormTextInput, useTheme } from '@liveconnect/components'

import { useBlockRouteChangeWithDialog } from '../../../../utils/routing/useBlockRouteChange'
import useDesignConstants from '../../hooks'
import useTenantDesign from '../../../../core/tenantDesign/useTenantDesign'
import useNotifications from '../../../../utils/notifications/useNotifications'
import useUi from '../../../../core/ui/useUi'

import { NavigationConfigurationFormProps } from './types'
import { Color, ConfigurationProps } from '../../types'

import './styles.scss'

const NavigationConfiguration: FC<ConfigurationProps> = ({
  colors,
  triggerCancel,
  formCancelled,
  triggerSubmit,
  formSubmitted,
  formDirty,
}) => {
  const { t } = useTranslation()
  const { changeThemeVars } = useTheme()
  const { defaultTheme } = useDesignConstants()
  const { editDesign } = useTenantDesign()
  const notify = useNotifications()
  const { showConfirmation } = useUi()

  const [showResetModal, setShowResetModal] = useState<boolean>(false)

  const methods = useForm<NavigationConfigurationFormProps>({
    mode: 'onChange',
    defaultValues: {
      'common-initial': colors['common-initial'],
      'common-hover': colors['common-hover'],
      'common-active': colors['common-active'],
      'common-disabled': colors['common-disabled'],
    },
  })

  const {
    control,
    reset,
    getValues,
    formState: { isDirty },
  } = methods

  const handleChangeColor = (color: Record<string, string>) => {
    changeThemeVars(color)
  }

  const handleResetColor = () => {
    reset()
    handleChangeColor({ ...getValues() })
    formCancelled()
    notify.success(t('tenantDesign.configuration.notification'))
  }

  const submitDesign = async () => {
    try {
      const data: Color[] = await Object.entries({ ...getValues() }).map(
        (color) => ({ key: color[0], value: color[1] })
      )
      await editDesign(data, 'navigation')
      reset({ ...getValues() })
      formSubmitted()
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    if (triggerCancel()) handleResetColor()
  }, [triggerCancel])

  useEffect(() => {
    if (triggerSubmit()) submitDesign()
  }, [triggerSubmit])

  useEffect(() => {
    formDirty(isDirty)
  }, [isDirty])

  useEffect(() => {
    showResetModal &&
      showConfirmation({
        title: t('tenantDesign.configuration.reset'),
        subtitle: t('tenantDesign.configuration.modal.body'),
        confirmText: t('tenantDesign.configuration.modal.reset'),
        cancelText: t('common.cancel'),
        iconName: 'report_problem',
        onConfirm: () => {
          handleResetColor()
          setShowResetModal(false)
        },
        onCancel: () => {
          setShowResetModal(false)
        },
      })
  }, [showResetModal])

  useEffect(() => {
    return () => changeThemeVars(defaultTheme)
  }, [])

  useBlockRouteChangeWithDialog(isDirty)

  return (
    <div className="navigation-design-configuration">
      <FormProvider methods={methods}>
        <div className="navigation-design-configuration__title">
          {t('tenantDesign.configuration.title')}
        </div>
        <hr className="navigation-design-configuration__separator" />
        <div className="navigation-design-configuration__body">
          <div className="navigation-design-configuration__body__picker d-flex align-items-center">
            <div className="navigation-design-configuration__body__picker__label">
              {t('tenantDesign.navigation.initial')}
            </div>
            <FormTextInput
              control={control}
              name="common-initial"
              type="color"
              onChange={(name, value) =>
                handleChangeColor({ [name]: value.toString() })
              }
            />
          </div>
          <div className="navigation-design-configuration__body__picker d-flex align-items-center">
            <div className="navigation-design-configuration__body__picker__label">
              {t('tenantDesign.navigation.hover')}
            </div>
            <FormTextInput
              control={control}
              name="common-hover"
              type="color"
              onChange={(name, value) =>
                handleChangeColor({ [name]: value.toString() })
              }
            />
          </div>
          <div className="navigation-design-configuration__body__picker d-flex align-items-center">
            <div className="navigation-design-configuration__body__picker__label">
              {t('tenantDesign.navigation.active')}
            </div>
            <FormTextInput
              control={control}
              name="common-active"
              type="color"
              onChange={(name, value) =>
                handleChangeColor({ [name]: value.toString() })
              }
            />
          </div>
          <div className="navigation-design-configuration__body__picker d-flex align-items-center">
            <div className="navigation-design-configuration__body__picker__label">
              {t('tenantDesign.navigation.disabled')}
            </div>
            <FormTextInput
              control={control}
              name="common-disabled"
              type="color"
              onChange={(name, value) =>
                handleChangeColor({ [name]: value.toString() })
              }
            />
          </div>
        </div>
        <div
          className="navigation-design-configuration__footer d-flex align-items-center"
          onClick={() => isDirty && setShowResetModal(true)}
        >
          <div className="navigation-design-configuration__footer__label">
            {t('tenantDesign.configuration.reset')}{' '}
          </div>
          <Icon
            className="navigation-design-configuration__footer__icon"
            name="upgrade"
          />
        </div>
      </FormProvider>
    </div>
  )
}

export default NavigationConfiguration
